/**
 * PURPOSE: Orden canónico de la vista tabla de Programación y N° impreso estable (FTR-0040).
 * RESPONSIBILITIES: Reproducir exactamente cómo la tabla ordena una jornada (pre-orden por idmatch →
 *   hora → BYE al final; una tabla por sede en orden de primera aparición; dentro de cada sede
 *   fecha → hora → nro_match) y construir el mapa idmatch → N° impreso aplicando la regla de FTR-0035
 *   a la jornada COMPLETA, para que un filtro nunca renumere. La vista normal y la filtrada usan estas
 *   mismas funciones, así que no pueden divergir.
 * COLLABORATORS: ProgramacionArea.jsx, programacionMatchNumber.js, programacionInstitutionFilter.js.
 * ROUTE: winscore/src/utils/programacionTableOrder.js
 */
import moment from "moment";

import { createMatchNumberResolver } from "./programacionMatchNumber.js";

export const isByeMatchRecord = (match) =>
  match?.flag_bye === true ||
  match?.flag_bye === 1 ||
  match?.flag_bye === "1" ||
  String(match?.flag_bye).toLowerCase() === "true";

// Misma lectura estricta de hora que la consulta de la jornada ("HH:mm:ss" o "HH:mm").
const strictTime = (value) => {
  if (!value) return null;
  const parsed = moment(value, ["HH:mm:ss", "HH:mm"], true);
  return parsed.isValid() ? parsed : null;
};

/**
 * Orden de la consulta de una jornada. El pre-orden por idmatch (FTR-0040) hace que los empates de
 * hora no dependan del orden de filas del backend (getmatches no tiene ORDER BY). Después: hora
 * (sin hora válida al final) y los BYE al final. `sort` es estable, así que el empate queda por idmatch.
 */
export const orderFetchedMatches = (fetched = []) => {
  const byId = [...fetched].sort((a, b) => Number(a?.idmatch) - Number(b?.idmatch));
  const normalMatches = byId.filter((match) => !isByeMatchRecord(match));
  const byeMatches = byId.filter((match) => isByeMatchRecord(match));

  normalMatches.sort((a, b) => {
    const timeA = strictTime(a.time_match);
    const timeB = strictTime(b.time_match);
    if (!timeA && !timeB) return 0;
    if (!timeA) return 1;
    if (!timeB) return -1;
    return timeA.valueOf() - timeB.valueOf();
  });

  return [...normalMatches, ...byeMatches];
};

// Dentro de una sede: fecha (sin fecha = 0, primero) → hora → nro_match. Idéntico a la tabla.
const compareWithinVenue = (a, b) => {
  const dateA = a.date_match ? new Date(a.date_match).getTime() : 0;
  const dateB = b.date_match ? new Date(b.date_match).getTime() : 0;
  if (dateA !== dateB) return dateA - dateB;

  const horaA = a.time_match || "";
  const horaB = b.time_match || "";
  if (horaA !== horaB) return horaA.localeCompare(horaB);

  return Number(a.nro_match || 0) - Number(b.nro_match || 0);
};

/** Tablas por sede, en el orden en que la vista tabla las pinta. */
export const groupRowsByVenue = (visibleMatches = []) => {
  const groups = new Map();

  for (const match of visibleMatches) {
    const sedeName = match?.sede?.name?.trim() || "Sin sede asignada";
    const key = match?.sede?.idsede ? `sede-${match.sede.idsede}` : `sede-${sedeName}`;
    if (!groups.has(key)) groups.set(key, { key, sedeName, matches: [] });
    groups.get(key).matches.push(match);
  }

  const list = [...groups.values()];
  list.forEach((group) => group.matches.sort(compareWithinVenue));
  return list;
};

/**
 * N° impreso de UNA jornada completa: idmatch → número ("" = sin número, la tabla muestra "-").
 * Recibe la jornada tal como la devuelve el backend, en cualquier orden.
 */
export const buildPrintedNumberMap = (jornadaMatches = []) => {
  const visible = orderFetchedMatches(jornadaMatches).filter((match) => !isByeMatchRecord(match));
  const resolve = createMatchNumberResolver(visible);
  const numbers = new Map();

  for (const group of groupRowsByVenue(visible)) {
    for (const match of group.matches) numbers.set(match.idmatch, resolve(match));
  }

  return numbers;
};

/**
 * Clave de jornada tal como la carga la vista normal: (fase, nro_fecha), o solo nro_fecha cuando la
 * consulta no filtra por fase (`shouldUseOldNoPhaseFilter`).
 */
export const jornadaKey = (match, { includePhase = true } = {}) => {
  const fecha = match?.nro_fecha ?? null;
  return includePhase ? `${match?.idtypephase ?? null}:${fecha}` : `*:${fecha}`;
};

/** N° impreso de todos los partidos de un torneo, calculado jornada por jornada. */
export const buildTournamentPrintedNumbers = (tournamentMatches = [], { includePhase = true } = {}) => {
  const byJornada = new Map();

  for (const match of tournamentMatches) {
    const key = jornadaKey(match, { includePhase });
    if (!byJornada.has(key)) byJornada.set(key, []);
    byJornada.get(key).push(match);
  }

  const numbers = new Map();
  for (const jornada of byJornada.values()) {
    for (const [idmatch, number] of buildPrintedNumberMap(jornada)) numbers.set(idmatch, number);
  }

  return numbers;
};
